import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Layouts
import AdminLayout from './components/AdminLayout';
import MemberLayout from './components/MemberLayout';

// Admin Pages
import Dashboard from './admin/Dashboard';
import Meeting from './admin/Meeting';
import SuperAdmin from './components/superadmin';
import Setting from './admin/Setting';
import Users from './admin/Users';
import Event from './admin/event';
import AttendanceReport from './admin/attendancereport';
import ChapterReport from './admin/chapterreport';
import ChapterNetworkingDetail from './admin/chapterNetworkingDetail';
import ChangePassword from './admin/ChangePassword';
import CommitteeProfile from './admin/Committee';
import EditCommittee from './admin/editcommittee';
import Membership from './admin/Membership';
import Tenure from './admin/Tenure';
import ActiveCodes from './admin/ActiveCodes';
import LandingPage from './admin/landingpagemanage';
import Communication from './admin/communication';
import ViewData from './admin/Viewdata';
import HelpCenterPage from './pages/admin/HelpCenterPage';
import Sponsor from './admin/Sponsor';
import Visitor from './admin/Visitor';
import PushNotifications from './admin/PushNotifications';


// Member Pages
import MemberHome from './member/memberhome';
import MemberData from './member/memberdata';
import MemberProfile from './member/memberprofile';
import OneToOne from './member/onetoone';
import BusinessExchange from './member/businessexchange';
import GiveReference from './member/givereference';

// Auth Pages
import Login from './auth/Login';
import MemberLogin from './auth/memberlogin';
import Home from './pages/home';
import AllMembers from './pages/allmembers';
import DeleteAccountDemo from './pages/DeleteAccountDemo';
import PrivacyPolicy from './pages/PrivacyPolicy';
import VisitorForm from './pages/VisitorForm';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkAuth = () => {
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    if (token && user) {
      setIsAuthenticated(true);
      if (user.role === 'super-admin' || user.role === 'admin' || user.role === 'sub-admin') {
        setUserRole('admin');
      } else {
        setUserRole('member');
      }
    } else {
      setIsAuthenticated(false);
      setUserRole(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  if (loading) {
    return <div className="flex h-screen items-center justify-center font-bold text-[#1A87CF]">Loading...</div>;
  }

  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route path="/" element={
        isAuthenticated
          ? (userRole === 'admin' ? <Navigate to="/admin/dashboard" replace /> : <Navigate to="/member/dashboard" replace />)
          : <Home />
      } />
      <Route path="/members" element={<AllMembers />} />
      <Route path="/delete-account" element={<DeleteAccountDemo />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/visitor" element={<VisitorForm />} />

      <Route path="/admin" element={
        isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <Login setIsAuthenticated={checkAuth} />
      } />

      <Route path="/member-login" element={
        isAuthenticated ? <Navigate to="/member/dashboard" replace /> : <MemberLogin setIsAuthenticated={checkAuth} />
      } />

      {/* MEMBER PROTECTED ROUTES */}
      {isAuthenticated && userRole === 'member' && (
        <Route path="/member/*" element={
          <MemberLayout>
            <Routes>
              <Route path="dashboard" element={<MemberHome />} />

              {/* ADD THIS LINE - Route for Member Data */}
              <Route path="memberdata" element={<MemberData />} />
              <Route path="memberprofile" element={<MemberProfile />} />
              <Route path="onetoone" element={<OneToOne />} />
              <Route path="businessexchange" element={<BusinessExchange />} />
              <Route path="givereference" element={<GiveReference />} />

              {/* Catch-all - Keep this at the end */}
              <Route path="*" element={<Navigate to="/member/dashboard" replace />} />
            </Routes>
          </MemberLayout>
        } />
      )}
      {/* ADMIN PROTECTED ROUTES */}
      {isAuthenticated && userRole === 'admin' && (
        <Route path="/admin/*" element={
          <AdminLayout>
            <Routes>
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="meeting" element={<Meeting />} />
              <Route path="events" element={<Event />} />
              <Route path="committee" element={<CommitteeProfile />} />
              <Route path="committee/edit" element={<EditCommittee />} />
              <Route path="tenure" element={<Tenure />} />
              <Route path="users" element={<Users />} />
              {/* Member detail and the add/edit form are pages, not modals — Users.js
                  renders the matching view for each of these paths. */}
              <Route path="users/new" element={<Users />} />
              <Route path="users/:id" element={<Users />} />
              <Route path="users/:id/edit" element={<Users />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="attendance-report" element={<AttendanceReport />} />
              <Route path="business-report" element={<ChapterReport />} />
              <Route path="business-report/:memberId/:type" element={<ChapterNetworkingDetail />} />
              <Route path="membership" element={<Membership />} />
              <Route path="view-data/:type" element={<ViewData />} />
              <Route path="active-codes" element={<ActiveCodes />} />
              {/* <Route path="communication" element={<Communication />} /> */}

              {/* Super Admin Only */}
              <Route element={<SuperAdmin />}>
                <Route path="banner-sponsor" element={<Sponsor />} />
              </Route>
              <Route element={<SuperAdmin />}>
                <Route path="help-center" element={<HelpCenterPage />} />
              </Route>
              <Route element={<SuperAdmin />}>
                <Route path="setting" element={<Setting />} />
              </Route>
               <Route element={<SuperAdmin />}>
                <Route path="landing-page" element={<LandingPage />} />
              </Route>
              <Route element={<SuperAdmin />}>
                <Route path="communication" element={<Communication />} />
              </Route>
              <Route element={<SuperAdmin />}>
                <Route path="push-notifications" element={<PushNotifications />} />
              </Route>
              <Route path="visitors" element={<Visitor />} />

              {/* FIX: Redirect to absolute path */}
              <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </AdminLayout>
        } />
      )}

      {/* DEFAULT REDIRECT */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;
